'use client';

/**
 * The header's mobile/tablet nav — a hamburger button plus a slide-in side
 * drawer. Desktop keeps the normal horizontal nav bar (.cb-part-nav);
 * customTheme.css's media query hides that bar and shows this toggle below
 * the breakpoint instead. The drawer renders the exact same nav node tree
 * (brand dropdowns included — see HoverGroup.tsx for how those become
 * tap-to-open on touch) so there's only one nav data source to keep in sync.
 */
import React, { useEffect, useState } from 'react';

interface MobileNavToggleProps {
    nav: React.ReactNode;
    search?: React.ReactNode;
}

const MobileNavToggle: React.FC<MobileNavToggleProps> = ({ nav, search }) => {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!open) return;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = '';
        };
    }, [open]);

    return (
        <>
            <button
                type="button"
                className="cb-mobile-nav-trigger"
                aria-label={open ? 'Close menu' : 'Open menu'}
                aria-expanded={open}
                onClick={() => setOpen((value) => !value)}
            >
                {open ? (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <line x1="5" y1="5" x2="19" y2="19" />
                        <line x1="19" y1="5" x2="5" y2="19" />
                    </svg>
                ) : (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <line x1="3" y1="6" x2="21" y2="6" />
                        <line x1="3" y1="12" x2="21" y2="12" />
                        <line x1="3" y1="18" x2="21" y2="18" />
                    </svg>
                )}
            </button>

            <div
                className={`cb-mobile-nav-backdrop ${open ? 'is-open' : ''}`}
                onClick={() => setOpen(false)}
                aria-hidden="true"
            />
            <div className={`cb-mobile-nav-drawer ${open ? 'is-open' : ''}`} onClick={() => setOpen(false)}>
                <div className="cb-mobile-nav-drawer-inner" onClick={(event) => event.stopPropagation()}>
                    {search && <div className="cb-mobile-nav-search">{search}</div>}
                    {nav}
                </div>
            </div>
        </>
    );
};

export default MobileNavToggle;
